import { AdGroupsPerformanceStatsTableItem, AdCopyPerformanceStatsTableItem, PositionInfos } from '../interfaces/stats'
import { AdGroupPerformanceStatsQuery, CampaignPerformanceStatsQuery } from '../__generated__/graphql'

type RowWithStats<S extends object> = {
  name: string
  id?: string
  slug?: string
  stats: S
}

export const mapData = <S extends object>(
  dataArray: RowWithStats<S>[] | undefined
): Array<S & { name: string; id?: string; slug?: string }> =>
  dataArray?.map(({ name, id, stats, slug }) => ({
    name,
    ...(slug ? { slug } : {}),
    ...(id ? { id } : {}),
    ...stats,
  })) ?? []

export const mapTotalData = <T,>(performanceStatsStats: T): T & { name: string } => ({
  name: 'Total',
  ...performanceStatsStats,
})

type TimeSeriesPoint = { date: string; stats: object }

export const mapTimeSeriesData = <T extends object>(
  timeSeriesCost: TimeSeriesPoint[] | undefined
): Array<T & { name: string }> => {
  if (!timeSeriesCost) return []

  return timeSeriesCost.map(({ date, stats }) => ({
    name: date,
    ...stats,
  })) as Array<T & { name: string }>
}

export const mapPositionData = <T extends object>(
  positionCost: (PositionInfos & { stats: T })[]
): Array<T & PositionInfos> =>
  positionCost?.map(({ name, status, stats, channelType, id }) => ({
    name,
    status,
    channelType,
    ...(id ? { id } : {}),
    ...stats,
  })) || []

export const mapPositionsAndWebsitesData = <T extends object>(
  websitesCost:
    | {
        name: string
        id?: string
        additionalInfo?: string
        stats: T
        positions?: (PositionInfos & { stats: T })[]
      }[]
    | undefined
): Array<T & { name: string; subRows?: Array<T & PositionInfos> }> =>
  websitesCost?.map(({ name, id, stats, positions, additionalInfo }) => ({
    name: name,
    ...(additionalInfo ? { additionalInfo } : {}),
    ...(id ? { id } : {}),
    ...stats,
    subRows: mapPositionData(positions ?? []),
  })) || []

type AdTemplateWithZoneStats<S extends object> = {
  id?: string
  name: string
  stats: S
  zones?: Array<{ name: string; stats: S }>
}

export const mapZonesWithAdTemplatesData = <T extends object>(
  zones: Array<{ name: string; stats: T }> | undefined,
  adTemplates: AdTemplateWithZoneStats<T>[] | undefined
): Array<T & { name: string; subRows?: Array<T & { name: string; id?: string }> }> =>
  zones?.map(({ name, stats }) => ({
    name,
    ...stats,
    subRows:
      adTemplates?.flatMap((adTemplate) => {
        const zoneStats = adTemplate.zones?.find((zone) => zone.name === name)
        if (!zoneStats) return []

        return [
          {
            name: adTemplate.name,
            ...(adTemplate.id ? { id: adTemplate.id } : {}),
            ...zoneStats.stats,
          },
        ]
      }) ?? [],
  })) ?? []

export const mapAdGroupsData = (
  adGroupsCost: CampaignPerformanceStatsQuery['campaign']['performanceStats']['adGroups'] | undefined
): Array<AdGroupsPerformanceStatsTableItem> =>
  adGroupsCost?.map(({ name, status, dailyBudget, stats, id }) => ({
    name,
    status,
    dailyBudget,
    id,
    ...stats,
  })) || []

export const mapAdCopiesData = (
  adCopiesCost: AdGroupPerformanceStatsQuery['adGroup']['performanceStats']['adCopies'] | undefined
): Array<AdCopyPerformanceStatsTableItem> =>
  adCopiesCost?.map((adCopy) => ({
    id: adCopy.id,
    name: adCopy.name,
    image: adCopy.image,
    status: adCopy.status,
    statusUpdatedAt: adCopy.statusUpdatedAt,
    title: adCopy.title,
    description: adCopy.description,
    callToAction: adCopy.callToAction,
    advertiserName: adCopy.advertiserName,
    ...adCopy.stats,
  })) || []
